'use client'

import { useId, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

// Accessible tabbed content for help articles. The tab *panels* are
// passed in pre-rendered (server components) as `panels`, so the
// article body stays server-rendered for SEO; this island only owns
// the active-tab state + keyboard handling (roving tabindex per the
// WAI-ARIA tabs pattern). The first tab is shown by default.
export function HelpTabs({
  labels,
  panels,
  title,
}: {
  labels: string[]
  panels: React.ReactNode[]
  title?: string
}) {
  const [active, setActive] = useState(0)
  const baseId = useId()
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = i
    if (e.key === 'ArrowRight') next = (i + 1) % labels.length
    else if (e.key === 'ArrowLeft') next = (i - 1 + labels.length) % labels.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = labels.length - 1
    else return
    e.preventDefault()
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <div className="not-prose my-7">
      {title && <h3 className="mb-3 font-display text-lg font-medium text-foreground">{title}</h3>}
      <div className="overflow-hidden rounded-lg border border-border/70 bg-card/40">
        <div
          role="tablist"
          aria-label={title || 'Tabbed content'}
          className="flex gap-1 overflow-x-auto border-b border-border/70 bg-secondary/30 p-1.5"
        >
          {labels.map((label, i) => {
            const selected = i === active
            return (
              <button
                key={label + i}
                ref={(el) => {
                  tabRefs.current[i] = el
                }}
                role="tab"
                id={`${baseId}-tab-${i}`}
                aria-selected={selected}
                aria-controls={`${baseId}-panel-${i}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(i)}
                onKeyDown={(e) => onKeyDown(e, i)}
                className={cn(
                  'whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                  selected
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
        {panels.map((panel, i) => (
          <div
            key={i}
            role="tabpanel"
            id={`${baseId}-panel-${i}`}
            aria-labelledby={`${baseId}-tab-${i}`}
            hidden={i !== active}
            tabIndex={0}
            className="px-5 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50"
          >
            {panel}
          </div>
        ))}
      </div>
    </div>
  )
}
